/**
 * SystemModuleAuditTab — F12.M Etap D Part 2 (generic audit log tab).
 *
 * Generyczna zakładka „Historia / Audit log" dla 13 kartotek systemowych: contractors,
 * contractor_contacts, companies, employees, users, products oraz 7 klas dokumentów
 * (purchase_invoices, sale_invoices, contracts, letters, commercial, internal, specjalne).
 * Zastępuje per-encja kopie (np. ContractorAuditTab) → ten sam komponent, props: moduleSlug +
 * recordId + testIdPrefix do zachowania backward-compat testów.
 *
 * Backend: GET /api/system-modules/:slug/records/:recordId/audit?limit=&offset=. Prawo do
 * historii = prawo do otwarcia rekordu (kto może otworzyć kartę, ten widzi historię) — odmowa
 * przychodzi z tymi samymi statusami co karta i kończy się tu stanem błędu.
 *
 * i18n: klucze pod `tenant.systemModuleAudit.*` (label akcji, tłumaczenia dla
 * action codes — fallback do raw action gdy brak tłumaczenia).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Skeleton } from '@/components/Skeleton';
import { api } from '@/lib/api';

const PAGE_SIZE = 20;

interface AuditEntry {
  id: string;
  action: string;
  userId: string | null;
  userEmail: string | null;
  displayName: string | null;
  details: Record<string, unknown> | null;
  ip: string | null;
  createdAt: string;
}

interface AuditResponse {
  entries: AuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

interface Props {
  moduleSlug: string;
  recordId: string;
  /** Prefix dla data-testid (np. „contractor-audit" dla backward-compat testów). */
  testIdPrefix?: string;
}

export function SystemModuleAuditTab({
  moduleSlug,
  recordId,
  testIdPrefix = 'system-module-audit',
}: Props) {
  const { t, i18n } = useTranslation();
  const [offset, setOffset] = useState(0);

  const q = useQuery({
    queryKey: ['system-module-audit', moduleSlug, recordId, offset],
    queryFn: async () => {
      const res = await api.get<AuditResponse>(
        `/system-modules/${moduleSlug}/records/${recordId}/audit?limit=${PAGE_SIZE}&offset=${offset}`,
      );
      return res.data;
    },
  });

  if (q.isLoading) {
    return (
      <div className="space-y-2 p-4" data-testid={`${testIdPrefix}-loading`}>
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (q.isError) {
    return (
      <div className="p-4 text-sm text-status-error" data-testid={`${testIdPrefix}-error`}>
        {t('tenant.systemModuleAudit.error')}
      </div>
    );
  }

  const data = q.data!;
  const entries = data.entries;
  const hasPrev = offset > 0;
  const hasNext = offset + data.limit < data.total;
  const fmtDate = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'short',
    timeStyle: 'short',
  });

  return (
    <div className="p-4 space-y-3" data-testid={`${testIdPrefix}-tab`}>
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-text-primary">
          {t('tenant.systemModuleAudit.title')}
        </h2>
        <span className="text-xs text-text-tertiary" data-testid={`${testIdPrefix}-total`}>
          {t('tenant.systemModuleAudit.total', { count: data.total })}
        </span>
      </div>

      {entries.length === 0 ? (
        <div
          className="rounded border border-border-secondary bg-bg-secondary p-6 text-center text-sm text-text-tertiary"
          data-testid={`${testIdPrefix}-empty`}
        >
          {t('tenant.systemModuleAudit.empty')}
        </div>
      ) : (
        <ul className="divide-y divide-border-secondary rounded border border-border-secondary">
          {entries.map((e) => (
            <li
              key={e.id}
              className="flex flex-col gap-1 p-3 text-sm"
              data-testid={`${testIdPrefix}-row-${e.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Badge color="blue">
                    {t(`tenant.systemModuleAudit.actions.${e.action}`, {
                      defaultValue: e.action,
                    })}
                  </Badge>
                  <span className="truncate text-text-primary">
                    {e.displayName ?? e.userEmail ?? t('tenant.systemModuleAudit.systemUser')}
                  </span>
                </div>
                <span className="text-xs text-text-tertiary whitespace-nowrap">
                  {fmtDate.format(new Date(e.createdAt))}
                </span>
              </div>
              {e.details && Object.keys(e.details).length > 0 && (
                <pre className="text-xs text-text-tertiary bg-bg-secondary rounded p-2 overflow-x-auto">
                  {JSON.stringify(e.details, null, 2)}
                </pre>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center justify-between">
        <Button
          variant="secondary"
          disabled={!hasPrev}
          onClick={() => setOffset(Math.max(0, offset - data.limit))}
          data-testid={`${testIdPrefix}-prev`}
        >
          {t('common.previous')}
        </Button>
        <span className="text-xs text-text-tertiary">
          {t('tenant.systemModuleAudit.pageInfo', {
            from: data.total === 0 ? 0 : offset + 1,
            to: Math.min(offset + data.limit, data.total),
            total: data.total,
          })}
        </span>
        <Button
          variant="secondary"
          disabled={!hasNext}
          onClick={() => setOffset(offset + data.limit)}
          data-testid={`${testIdPrefix}-next`}
        >
          {t('common.next')}
        </Button>
      </div>
    </div>
  );
}
